import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Button, TextField, ButtonGroup } from '@mui/material';
import PanelShell from './PanelShell';
import VariableAutocomplete from '../VariableAutocomplete';

const AlarmPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();

  const [alarmMessage, setAlarmMessage] = useState(data?.message || '');
  const [alarmSeverity, setAlarmSeverity] = useState(data?.severity || 'Medium');
  const [selectedVariables, setSelectedVariables] = useState(data?.variables || []);

  useEffect(() => {
    setAlarmMessage(data?.message || '');
    setAlarmSeverity(data?.severity || 'Medium');
    setSelectedVariables(data?.variables || []);
  }, [data]);

  // RMMS-themed severity levels
  const severityLevels = [
    { value: 'Low', label: 'Low', color: '#FFE082', textColor: '#000' },
    { value: 'Medium', label: 'Medium', color: '#FFB74D', textColor: '#000' },
    { value: 'High', label: 'High', color: '#EF5350', textColor: '#fff' },
    { value: 'Critical', label: 'Critical', color: '#6a1b9a', textColor: '#fff' } // RMMS accent
  ];

  const handleVariableSelect = (event, variable) => {
    if (!variable) return;

    const variableName = variable.displayName || variable.name;

    setAlarmMessage(prevMessage => {
      const input = document.getElementById('alarmMessage');
      const cursorPosition = input?.selectionStart || prevMessage.length;
      const newMessage =
        prevMessage.slice(0, cursorPosition) +
        `{${variableName}}` +
        prevMessage.slice(cursorPosition);
      return newMessage;
    });

    setSelectedVariables(prev => {
      if (!prev.find(v => v.id === variable.id)) {
        return [...prev, variable];
      }
      return prev;
    });
  };

  const handleSave = () => {
    const title = `Alarm - ${alarmSeverity}`;

    const updatedData = {
      ...data,
      message: alarmMessage,
      severity: alarmSeverity,
      variables: selectedVariables,
      Title: title,
      label: 'Alarm',
      icon: 'alarm-icon',
      ElementDetail: selectedVariables.map(v => ({
        elementDetailVariableId: v.id,
        elementDetailVariableType: v.type
      }))
    };

    onChange(updatedData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({ type: 'success', message: 'Alarm tanımı kaydedildi.' }));
  };

  if (nodeType !== 'Alarm') return null;

  return (
    <PanelShell title="Alarm" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%', overflow: 'hidden' }}>
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Alarm Message
          </Typography>
          <TextField
            id="alarmMessage"
            fullWidth
            multiline
            rows={4}
            size="small"
            value={alarmMessage}
            onChange={(e) => setAlarmMessage(e.target.value)}
            placeholder="Enter alarm message... Select variables below to add them"
          />
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Add Variable to Message
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={handleVariableSelect}
            placeholder="Search tags and variables..."
            showConstants={false}
          />
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Alarm Level
          </Typography>
          <ButtonGroup fullWidth variant="contained" size="small">
            {severityLevels.map((level) => (
              <Button
                key={level.value}
                onClick={() => setAlarmSeverity(level.value)}
                sx={{
                  backgroundColor: level.color,
                  color: level.textColor,
                  border: alarmSeverity === level.value ? '3px solid #000' : '1px solid #ccc',
                  fontWeight: alarmSeverity === level.value ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: level.color,
                    opacity: 0.85,
                    transform: 'scale(1.02)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {level.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {selectedVariables.length > 0 && (
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
              Selected Variables ({selectedVariables.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedVariables.map((v, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem'
                  }}
                >
                  {v.tagName || v.varName || v.name}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </PanelShell>
  );
};

export default AlarmPanel;
